'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) return;

    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = [];
      window.gtag = function (...args: any[]) {
        window.dataLayer.push(args);
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        page_location: window.location.href,
        page_title: document.title,
      });
    }

    const url = pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    window.gtag('event', 'page_view', {
      page_path: url,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// Conversion events
export function trackEnterpriseInquirySubmit(budget: string) {
  trackEvent('enterprise_inquiry_submit', {
    event_category: 'conversion',
    event_label: 'enterprise_inquiry',
    budget_range: budget,
  });
}

export function trackROICalculatorInteraction(budget: number, reviews: number) {
  trackEvent('roi_calculator_use', {
    event_category: 'engagement',
    event_label: 'roi_calculator',
    budget_value: budget,
    estimated_reviews: reviews,
  });
}

export function trackROICalculatorSubmit(budget: number) {
  trackEvent('roi_calculator_submit', {
    event_category: 'conversion',
    event_label: 'roi_calculator_lead',
    budget_value: budget,
  });
}

export function trackContactFormSubmit(inquiryType: string) {
  trackEvent('contact_form_submit', {
    event_category: 'conversion',
    event_label: 'contact_form',
    inquiry_type: inquiryType,
  });
}

export function trackReviewerSignup() {
  trackEvent('reviewer_signup_start', {
    event_category: 'conversion',
    event_label: 'reviewer_funnel',
  });
}

export function trackCampaignView(campaignId: string, campaignName: string) {
  trackEvent('campaign_view', {
    event_category: 'engagement',
    event_label: campaignName,
    campaign_id: campaignId,
  });
}

export function trackCampaignApply(campaignId: string, campaignName: string) {
  trackEvent('campaign_apply', {
    event_category: 'conversion',
    event_label: campaignName,
    campaign_id: campaignId,
  });
}
